import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message, List, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import platformRecordApi from '../../../services/process/platform-record';
import { PlatformRecord } from '../../../types/process/platform-record';
import { useStaffMap } from '../../../services/store/organization.store';

const { TextArea } = Input;
const { Text } = Typography;

export interface PlatformLogModalProps {
  visible: boolean;
  onCancel: () => void;
  orderNo: string;
  type?: string; // default 'urge_delivery'
  title?: string;
  onSuccess?: () => void;
}

const PlatformLogModal: React.FC<PlatformLogModalProps> = ({
  visible,
  onCancel,
  orderNo,
  type = 'urge_delivery',
  title = '催促发货跟进',
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState<PlatformRecord[]>([]);
  const [content, setContent] = useState('');
  const { staffMap } = useStaffMap();

  const fetchRecords = async () => {
    if (!orderNo || !visible) return;
    setLoading(true);
    try {
      const res = await platformRecordApi.list({
        filter: {
          related_id: { operator: 'eq', value: orderNo },
          type: { operator: 'eq', value: type },
        },
        sort: [{ field: 'created_at', order: 'desc' }],
      });
      // BaseResponse<ListResponse<PlatformRecord>>
      // res.data is ListResponse, res.data.items is array
      setRecords(res.data?.items || []);
    } catch (error) {
      console.error(error);
      message.error('获取记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchRecords();
      setContent('');
    }
  }, [visible, orderNo, type]);

  const handleSave = async () => {
    if (!content.trim()) {
      message.warning('请输入跟进记录');
      return;
    }
    setSaving(true);
    try {
      await platformRecordApi.create({
        type,
        relatedId: orderNo,
        content: content.trim(),
      });
      message.success('保存成功');
      setContent('');
      fetchRecords();
      onSuccess?.();
    } catch (error) {
      console.error(error);
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <span style={{ fontSize: 18, fontWeight: 600, color: '#0072fa' }}>{title} - 订单 <Text copyable>{orderNo}</Text></span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={500}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">记录{title}的沟通情况。</Text>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 500, marginBottom: 8 }}>历史跟进记录</div>
        <div style={{ 
          background: '#f8fafc', 
          borderRadius: 8, 
          padding: '12px 16px', 
          height: 200, 
          overflowY: 'auto',
          border: '1px solid #f0f0f0'
        }}>
          <List
            loading={loading}
            dataSource={records}
            locale={{ emptyText: '暂无记录' }}
            split={false}
            renderItem={(item) => {
              const staffName = staffMap.get(item.operatorId)?.name || item.operatorId;
              return (
                <div style={{ marginBottom: 12, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{staffName}</Text>
                  </div>
                  <div style={{ fontSize: 14, color: '#0a0a0a' }}>{item.content}</div>
                </div>
              );
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 500, marginBottom: 8 }}>新增跟进记录</div>
        <TextArea
          rows={3}
          placeholder="例如：已联系采购人员，预计明天发货..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={500}
          showCount
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <Button onClick={onCancel}>关闭</Button>
        <Button type="primary" onClick={handleSave} loading={saving}>保存记录</Button>
      </div>
    </Modal>
  );
};

export default PlatformLogModal;
